import { AccountsPanel } from './AccountsPanel'
import { useEffect, useState } from 'react'
import {
  Settings, FolderOpen, LogOut,
  AlertCircle, ChevronRight, Cloud, Loader2, X, Palette, Save,
  Radio, RotateCw, Copy,
} from 'lucide-react'
import { authApi, broadcastApi, folderApi, settingsApi } from '../../services/api'
import { useAuthStore, usePlaybackStore, useSettingsStore } from '../../store'
import { cleanFilename, formatDuration, getCompatibilityInfo } from '../../utils'
import type { CloudFolder, CloudProvider } from '../../types'
import { APP_VERSION_LABEL } from '../../version'
import clsx from 'clsx'
import { DiagnosticsPanel } from './DiagnosticsPanel'

interface SettingsPanelProps {
  onClose: () => void
  onFolderSelected: () => void
}

type Step = 'provider' | 'folder' | 'done'

const BRAND_SWATCHES = [
  '#3B8BEB',
  '#2ECC8A',
  '#F5A623',
  '#E84545',
  '#A855F7',
  '#14B8A6',
]

export function SettingsPanel({ onClose, onFolderSelected }: SettingsPanelProps) {
  const [step, setStep] = useState<Step>('provider')
  const [selectedProvider, setSelectedProvider] = useState<CloudProvider | null>(null)
  const [folders, setFolders] = useState<CloudFolder[]>([])
  const [loadingFolders, setLoadingFolders] = useState(false)
  const [folderError, setFolderError] = useState<string | null>(null)
  const [connecting, setConnecting] = useState<CloudProvider | null>(null)
  const [appName, setAppName] = useState('CloudPlayer')
  const [accentColor, setAccentColor] = useState('#3B8BEB')
  const [welcomeMessage, setWelcomeMessage] = useState('')
  const [bumperEnabled, setBumperEnabled] = useState(false)
  const [bumperVideoId, setBumperVideoId] = useState('')
  const [bumperFrequency, setBumperFrequency] = useState(3)
  const [bumperPlacement, setBumperPlacement] = useState<'before_first' | 'after_every_n' | 'playlist_end'>('after_every_n')
  const [bumperSkipAllowed, setBumperSkipAllowed] = useState(true)
  const [bumperVolume, setBumperVolume] = useState(1)
  const [savingBranding, setSavingBranding] = useState(false)
  const [brandingError, setBrandingError] = useState<string | null>(null)
  const [isOwner, setIsOwner] = useState(false)
  const [broadcastUrl, setBroadcastUrl] = useState('')
  const [broadcastEnabled, setBroadcastEnabled] = useState(false)
  const [broadcastBusy, setBroadcastBusy] = useState(false)
  const [broadcastCopied, setBroadcastCopied] = useState(false)
  const [broadcastError, setBroadcastError] = useState<string | null>(null)

  const { google, setProviderAuth } = useAuthStore()
  const { settings, setSettings } = useSettingsStore()
  const { playlist } = usePlaybackStore()

  const selectedBumperVideo = playlist.find((item) => item.id === bumperVideoId) ?? null
  const bumperCompatibility = selectedBumperVideo
    ? getCompatibilityInfo(selectedBumperVideo, { bumper: true })
    : null
  useEffect(() => {
    setAppName(settings?.appName || 'CloudPlayer')
    setAccentColor(settings?.accentColor || '#3B8BEB')
    setWelcomeMessage(settings?.welcomeMessage || '')
    setBumperEnabled(settings?.bumperEnabled ?? false)
    setBumperVideoId(settings?.bumperVideoId || '')
    setBumperFrequency(settings?.bumperFrequency ?? 3)
    setBumperPlacement(settings?.bumperPlacement ?? 'after_every_n')
    setBumperSkipAllowed(settings?.bumperSkipAllowed ?? true)
    setBumperVolume(settings?.bumperVolume ?? 1)
  }, [settings])

  useEffect(() => {
    void authApi.getStatus().then(async (status) => {
      setIsOwner(status.viewer.isOwner)
      if (status.viewer.isOwner) {
        const broadcast = await broadcastApi.getConfig()
        setBroadcastUrl(broadcast.url)
        setBroadcastEnabled(broadcast.enabled)
      }
    }).catch(() => undefined)
  }, [])

  const handleUpdateBroadcast = async (enabled: boolean) => {
    setBroadcastBusy(true)
    setBroadcastError(null)
    setBroadcastCopied(false)
    try {
      const config = await broadcastApi.rotate(enabled)
      setBroadcastUrl(config.url)
      setBroadcastEnabled(config.enabled)
    } catch (error) {
      setBroadcastError(error instanceof Error ? error.message : 'Could not update broadcast output')
    } finally {
      setBroadcastBusy(false)
    }
  }

  const handleCopyBroadcastUrl = async () => {
    if (!broadcastUrl) return
    try {
      await navigator.clipboard.writeText(broadcastUrl)
      setBroadcastCopied(true)
      window.setTimeout(() => setBroadcastCopied(false), 1600)
    } catch {
      setBroadcastError('Could not copy URL')
    }
  }

  const handleConnectGoogle = async () => {
    setConnecting('google')
    try {
      const { url } = await authApi.getGoogleAuthUrl()
      const popup = window.open(url, 'oauth', 'width=500,height=600')

      // Poll for completion
      const check = setInterval(async () => {
        if (popup?.closed) {
          clearInterval(check)
          setConnecting(null)
          const status = await authApi.getStatus()
          setProviderAuth('google', status.google)
        }
      }, 1000)
    } catch (e) {
      setConnecting(null)
    }
  }

  const handleDisconnect = async (provider: CloudProvider) => {
    await authApi.disconnect(provider)
    setProviderAuth(provider, { connected: false, expiresAt: null, email: null })
  }

  const handleSelectProvider = async (provider: CloudProvider) => {
    setSelectedProvider(provider)
    setStep('folder')
    setLoadingFolders(true)
    setFolderError(null)

    try {
      const list = await folderApi.listFolders(provider)
      setFolders(list)
    } catch (e) {
      setFolderError(e instanceof Error ? e.message : 'Failed to load folders')
    } finally {
      setLoadingFolders(false)
    }
  }

  const handleSelectFolder = async (folder: CloudFolder) => {
    if (!selectedProvider) return
    await folderApi.selectFolder(selectedProvider, folder.id, folder.name, folder.path)
    setStep('done')
    onFolderSelected()
  }

  const handleSaveBranding = async () => {
    setSavingBranding(true)
    setBrandingError(null)
    try {
      const updated = await settingsApi.updateSettings({
        appName,
        accentColor,
        welcomeMessage: welcomeMessage.trim() || null,
        bumperEnabled,
        bumperVideoId: bumperEnabled ? bumperVideoId || null : null,
        bumperFrequency,
        bumperPlacement,
        bumperSkipAllowed,
        bumperVolume,
      })
      setSettings(updated)
    } catch (error) {
      setBrandingError(error instanceof Error ? error.message : 'Could not save branding')
    } finally {
      setSavingBranding(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm animate-fade-in">
      <div className="panel mx-4 flex max-h-[calc(100dvh-2rem)] w-full max-w-lg flex-col overflow-hidden shadow-2xl">

        {/* Header */}
        <div className="flex flex-shrink-0 items-center justify-between border-b border-border p-5">
          <div className="flex items-center gap-2">
            <Settings size={16} className="text-accent" />
            <h2 className="font-semibold text-bright">Setup</h2>
            <span className="rounded border border-border px-1.5 py-0.5 font-mono text-[8px] text-dim">
              {APP_VERSION_LABEL}
            </span>
          </div>
          <button
            onClick={onClose}
            className="btn-ghost p-1.5 rounded-lg"
            aria-label="Close setup"
          >
            <X size={16} />
          </button>
        </div>

        {/* Content */}
        <div className="min-h-0 flex-1 space-y-5 overflow-y-auto overscroll-contain p-5">

          {isOwner && <AccountsPanel />}

          {/* Branding */}
          <section>
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-xs font-semibold text-dim uppercase tracking-wider">
                Branding
              </h3>
              <div className="flex items-center gap-2 text-xs text-muted">
                <Palette size={13} />
                Room profile
              </div>
            </div>

            <div className="space-y-3">
              <label className="block">
                <span className="text-xs text-muted">App name</span>
                <input
                  type="text"
                  value={appName}
                  onChange={(event) => setAppName(event.target.value)}
                  maxLength={48}
                  className="input mt-1"
                  placeholder="CloudPlayer"
                />
              </label>

              <label className="block">
                <span className="text-xs text-muted">Welcome message</span>
                <textarea
                  value={welcomeMessage}
                  onChange={(event) => setWelcomeMessage(event.target.value)}
                  maxLength={160}
                  rows={2}
                  className="input mt-1 resize-none"
                  placeholder="Select a video from the playlist"
                />
              </label>

              <div>
                <span className="text-xs text-muted">Accent color</span>
                <div className="mt-2 flex items-center gap-2">
                  {BRAND_SWATCHES.map((color) => (
                    <button
                      key={color}
                      onClick={() => setAccentColor(color)}
                      className={clsx(
                        'h-7 w-7 rounded border transition-transform',
                        accentColor.toUpperCase() === color
                          ? 'border-bright scale-110'
                          : 'border-border hover:border-soft',
                      )}
                      style={{ backgroundColor: color }}
                      title={color}
                    />
                  ))}
                  <input
                    type="color"
                    value={accentColor}
                    onChange={(event) => setAccentColor(event.target.value.toUpperCase())}
                    className="h-7 w-9 rounded border border-border bg-surface"
                    title="Custom accent color"
                  />
                </div>
              </div>

              {brandingError && (
                <p className="text-xs text-danger">{brandingError}</p>
              )}

              <button
                onClick={handleSaveBranding}
                disabled={savingBranding}
                className="btn-primary w-full justify-center"
              >
                {savingBranding ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
                Save room settings
              </button>
            </div>
          </section>

          {/* Bumpers */}
          <section>
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-xs font-semibold text-dim uppercase tracking-wider">
                Bumpers
              </h3>
              <label className="flex items-center gap-2 text-xs text-muted">
                <input
                  type="checkbox"
                  checked={bumperEnabled}
                  onChange={(event) => setBumperEnabled(event.target.checked)}
                  className="accent-current"
                />
                Enabled
              </label>
            </div>

            <div className="space-y-3 rounded-lg border border-border bg-surface p-3">
              <label className="block">
                <span className="text-xs text-muted">Bumper clip</span>
                <select
                  value={bumperVideoId}
                  onChange={(event) => setBumperVideoId(event.target.value)}
                  disabled={!bumperEnabled || playlist.length === 0}
                  className="input mt-1"
                >
                  <option value="">
                    {playlist.length > 0 ? 'Select a video…' : 'Load a folder first'}
                  </option>
                  {playlist.map((item) => (
                    <option key={item.id} value={item.id}>
                      {cleanFilename(item.name)} · {formatDuration(item.durationSeconds)}
                    </option>
                  ))}
                </select>
              </label>

              <label className="block">
                <span className="text-xs text-muted">Placement</span>
                <select
                  value={bumperPlacement}
                  onChange={(event) => setBumperPlacement(event.target.value as typeof bumperPlacement)}
                  disabled={!bumperEnabled}
                  className="input mt-1"
                >
                  <option value="before_first">Before first video</option>
                  <option value="after_every_n">After every N videos</option>
                  <option value="playlist_end">At playlist end</option>
                </select>
              </label>

              {bumperPlacement === 'after_every_n' && <label className="block">
                <span className="text-xs text-muted">Play after every</span>
                <div className="mt-1 flex items-center gap-2">
                  <input
                    type="number"
                    min={1}
                    max={20}
                    value={bumperFrequency}
                    onChange={(event) => setBumperFrequency(Number(event.target.value))}
                    disabled={!bumperEnabled}
                    className="input w-20"
                  />
                  <span className="text-sm text-muted">videos</span>
                </div>
              </label>}

              <label className="block">
                <span className="text-xs text-muted">Bumper volume · {Math.round(bumperVolume * 100)}%</span>
                <input type="range" min={0} max={1} step={0.05} value={bumperVolume}
                  onChange={(event) => setBumperVolume(Number(event.target.value))}
                  disabled={!bumperEnabled} className="mt-1 w-full accent-current" />
              </label>

              <label className="flex items-center gap-2 text-xs text-muted">
                <input type="checkbox" checked={bumperSkipAllowed}
                  onChange={(event) => setBumperSkipAllowed(event.target.checked)}
                  disabled={!bumperEnabled} className="accent-current" />
                Allow viewers to skip the bumper
              </label>

              <p className="text-xs text-muted">
                Keep bumpers short and web-safe. MP4 with H.264/AAC is the least surprising choice.
              </p>

              {bumperCompatibility && (
                <div className={clsx(
                  'rounded border px-3 py-2 text-xs',
                  bumperCompatibility.status === 'likely' && 'border-success/35 bg-success/10 text-success',
                  bumperCompatibility.status === 'warning' && 'border-warning/45 bg-warning/10 text-warning',
                  bumperCompatibility.status === 'unsupported' && 'border-danger/45 bg-danger/10 text-danger',
                )}>
                  <p className="font-semibold">
                    {bumperCompatibility.label} · {bumperCompatibility.container}
                  </p>
                  <p className="mt-1 text-current/80">
                    {bumperCompatibility.notes[0]}
                  </p>
                </div>
              )}
            </div>
          </section>

          <DiagnosticsPanel />

          {isOwner && (
            <section>
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-xs font-semibold text-dim uppercase tracking-wider">
                  Broadcast output
                </h3>
                <div className="flex items-center gap-2 text-xs text-muted">
                  <Radio size={13} />
                  OBS / meeting source
                </div>
              </div>

              <div className="space-y-3 rounded-lg border border-border bg-surface p-3">
                <label className="flex items-center gap-2 text-xs text-muted">
                  <input
                    type="checkbox"
                    checked={broadcastEnabled}
                    onChange={(event) => void handleUpdateBroadcast(event.target.checked)}
                    disabled={broadcastBusy}
                    className="accent-current"
                  />
                  Public token URL enabled
                </label>

                <div className="flex gap-2">
                  <input
                    type="text"
                    value={broadcastUrl}
                    readOnly
                    className="input flex-1 font-mono text-xs"
                    placeholder="Create a broadcast URL"
                  />
                  <button
                    onClick={() => void handleCopyBroadcastUrl()}
                    disabled={!broadcastUrl}
                    className="btn-ghost px-2.5 py-1.5 rounded border border-border disabled:opacity-45"
                    title="Copy broadcast URL"
                  >
                    <Copy size={13} />
                  </button>
                  <button
                    onClick={() => void handleUpdateBroadcast(true)}
                    disabled={broadcastBusy}
                    className="btn-ghost px-2.5 py-1.5 rounded border border-border"
                    title="Rotate broadcast URL"
                  >
                    {broadcastBusy ? <Loader2 size={13} className="animate-spin" /> : <RotateCw size={13} />}
                  </button>
                </div>

                <p className="text-xs text-muted">
                  Use this URL as an OBS browser source. Rotate it when a link should stop working.
                </p>
                {broadcastCopied && <p className="text-xs text-success">Broadcast URL copied.</p>}
                {broadcastError && <p className="text-xs text-danger">{broadcastError}</p>}
              </div>
            </section>
          )}

          {/* Provider auth section */}
          <section>
            <h3 className="text-xs font-semibold text-dim uppercase tracking-wider mb-3">
              Cloud Storage
            </h3>
            <div className="space-y-2">
              <ProviderRow
                name="Google Drive"
                provider="google"
                connected={google.connected}
                email={google.email}
                connecting={connecting === 'google'}
                onConnect={handleConnectGoogle}
                onDisconnect={() => handleDisconnect('google')}
                onSelect={() => handleSelectProvider('google')}
                isSelected={settings?.provider === 'google'}
              />
            </div>
          </section>

          {/* Folder picker */}
          {step === 'folder' && selectedProvider && (
            <section>
              <h3 className="text-xs font-semibold text-dim uppercase tracking-wider mb-3">
                Select Folder
              </h3>
              {loadingFolders ? (
                <div className="flex items-center gap-2 text-muted py-4">
                  <Loader2 size={16} className="animate-spin" />
                  <span className="text-sm">Loading folders…</span>
                </div>
              ) : folderError ? (
                <div className="flex items-center gap-2 text-danger text-sm py-2">
                  <AlertCircle size={14} />
                  {folderError}
                </div>
              ) : (
                <div className="border border-border rounded-lg overflow-hidden max-h-48 overflow-y-auto">
                  {folders.map((folder) => (
                    <button
                      key={folder.id}
                      onClick={() => handleSelectFolder(folder)}
                      className="w-full flex items-center gap-3 px-4 py-3 hover:bg-border transition-colors text-left border-b border-border/50 last:border-0"
                    >
                      <FolderOpen size={15} className="text-warning flex-shrink-0" />
                      <span className="text-sm text-text flex-1 truncate">{folder.name}</span>
                      <ChevronRight size={14} className="text-muted" />
                    </button>
                  ))}
                  {folders.length === 0 && (
                    <p className="text-sm text-muted px-4 py-3">No folders found</p>
                  )}
                </div>
              )}
            </section>
          )}

          {/* Current selection */}
          {settings?.folderId && (
            <section className="bg-surface rounded-lg p-4 border border-border">
              <p className="text-xs text-dim mb-1">Currently playing from</p>
              <p className="text-sm font-medium text-text">
                {settings.provider === 'google' ? '🟡' : '🔵'} {settings.folderName}
              </p>
              {settings.folderPath && (
                <p className="text-xs text-muted mt-0.5 font-mono">{settings.folderPath}</p>
              )}
            </section>
          )}
        </div>
      </div>
    </div>
  )
}

interface ProviderRowProps {
  name: string
  provider: CloudProvider
  connected: boolean
  email: string | null
  connecting: boolean
  isSelected: boolean
  onConnect: () => void
  onDisconnect: () => void
  onSelect: () => void
}

function ProviderRow({
  name, connected, email, connecting, isSelected,
  onConnect, onDisconnect, onSelect,
}: ProviderRowProps) {
  return (
    <div className={clsx(
      'flex items-center gap-3 p-3 rounded-lg border transition-colors',
      isSelected ? 'border-accent/40 bg-accent/5' : 'border-border bg-surface',
    )}>
      <Cloud size={16} className={connected ? 'text-success' : 'text-muted'} />
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-text">{name}</p>
        {connected && email && (
          <p className="text-xs text-muted truncate">{email}</p>
        )}
        {!connected && (
          <p className="text-xs text-muted">Not connected</p>
        )}
      </div>

      <div className="flex gap-2">
        {connected ? (
          <>
            <button onClick={onSelect} className="btn-primary text-xs py-1 px-3">
              Select folder
            </button>
            <button onClick={onDisconnect} className="btn-ghost text-xs py-1 px-2">
              <LogOut size={13} />
            </button>
          </>
        ) : (
          <button
            onClick={onConnect}
            disabled={connecting}
            className="btn-ghost text-xs py-1 px-3 border border-border"
          >
            {connecting ? <Loader2 size={13} className="animate-spin" /> : 'Connect'}
          </button>
        )}
      </div>
    </div>
  )
}
